import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import { ArrowLeft, FileText, Loader2, Sparkles } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Alert } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Select } from "@/components/ui/select"

type PeriodOption = { value: string; label: string }

export default function AdminBriefsNew({
  period_options,
  assembler_enabled,
  errors,
}: {
  period_options: PeriodOption[]
  assembler_enabled: boolean
  errors?: Record<string, string>
}) {
  const [period, setPeriod] = React.useState(period_options[0]?.value ?? "")
  const [submitting, setSubmitting] = React.useState(false)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!period || submitting) return
    setSubmitting(true)
    router.post("/admin/briefs", { period }, { onFinish: () => setSubmitting(false) })
  }

  return (
    <>
      <Head title="Buat Brief Mingguan">
        <meta
          name="description"
          content="Pilih periode lalu generate brief mingguan dari metrik yang sudah direkonsiliasi."
        />
        <meta property="og:title" content="Buat Brief Mingguan" />
        <meta
          property="og:description"
          content="Pilih periode lalu generate brief mingguan dari metrik yang sudah direkonsiliasi."
        />
      </Head>
      <AppShell width="default">
        <div className="mb-6">
          <Link href="/admin/briefs" className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-accent">
            <ArrowLeft className="h-4 w-4" />
            Kembali ke daftar brief
          </Link>
        </div>

        <div className="flex items-start gap-3 border-b border-hairline pb-6">
          <FileText className="mt-1 h-6 w-6 shrink-0 text-ink-muted" />
          <div>
            <h1>Buat Brief Mingguan</h1>
            <p className="mt-1">
              Pilih periode (bulan). Sistem menghitung snapshot metrik vs bulan sebelumnya,
              mendeteksi sinyal, lalu — bila narasi AI aktif — menarasikan draf 3 bagian yang
              setiap angkanya tersitasi.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-6 space-y-5">
          <div className="max-w-xs">
            <label htmlFor="period">Periode brief</label>
            <Select
              id="period"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              disabled={submitting || period_options.length === 0}
              className="mt-1"
            >
              {period_options.length === 0 && <option value="">Belum ada periode metrik</option>}
              {period_options.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
            {errors?.period && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.period}</p>}
            <p className="mt-1 text-xs text-ink-muted">
              Default adalah periode termutakhir dengan data metrik. Perbandingan memakai bulan
              sebelumnya (MoM).
            </p>
          </div>

          {!assembler_enabled && (
            <Alert tone="warning" title="Narasi AI nonaktif">
              Kill-switch <code>WEEKLY_BRIEF</code> sedang mati (atau API key belum dikonfigurasi).
              Brief tetap dibuat sampai lapisan sinyal deterministik (status “Sinyal siap”), tanpa
              narasi/foresight/action item. Aktifkan kill-switch untuk narasi penuh.
            </Alert>
          )}

          <Button type="submit" variant="primary" disabled={submitting || !period} className="gap-2">
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Memulai…
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Generate Brief
              </>
            )}
          </Button>
        </form>
      </AppShell>
    </>
  )
}
